import { useEffect, useState } from "react";

import {
  PlusCircle,
  XCircleFill,
} from "@hl/base-components/lib/assets/icons.generated";
import CheckboxConfirm from "@hl/base-components/lib/inputs/CheckboxConfirm";
import { OUTLINE_COLOR } from "@hl/base-components/lib/theme/button";
import { TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import { WEIGHT_BOLD } from "@hl/base-components/lib/theme/typography";
import { SupportedMediaType } from "@hl/communities-app/lib/apollo/graphql.generated";
import {
  Box,
  Button,
  Grid,
  Group,
  Portal,
  Space,
  Stack,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { isEmpty } from "lodash";

import { DraftProgress, _CollectionType } from "apollo/graphql.generated";
import { typeFromMime } from "utils/media/file";
import SaveDraftButton from "~/features/collections/forms/CreateCollectionMultistepForm/SaveDraftButton";
import {
  useCreateCollectionFormContext,
  useCreateCollectionState,
} from "~/features/collections/forms/CreateCollectionMultistepForm/context";
import useSaveDraft from "~/features/collections/forms/CreateCollectionMultistepForm/useSaveDraft";
import CustomPaginationWrapper from "~/features/tokens/components/CustomPaginationWrapper";

import AddMetadataTraitsModal from "./AddMetadataTraitsModal";
import CollectionMediaUploader from "./CollectionMediaUploader";
import FormTitle from "./FormTitle";
import NextStepButton from "./NextStepButton";

const SelectSingleImageForm = ({ asQuickDropFlow = false }) => {
  const { dispatch } = useCreateCollectionState();
  const form = useCreateCollectionFormContext();
  const formValues = form.values;
  const theme = useMantineTheme();

  const [activePage, setPage] = useState(1);
  const [lowerBound, setLowerBound] = useState(1);
  const [upperBound, setUpperBound] = useState(1);
  const [addMetadataTraitsModalOpen, setAddMetadataTraitsModalOpen] =
    useState<boolean>(false);

  useEffect(() => {
    if (asQuickDropFlow) return;

    if (form.errors) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [form.errors, asQuickDropFlow]);

  const { saveDraft, loading } = useSaveDraft({
    furthestStep: DraftProgress.ADDED_METADATA,
  });

  const onNextStep = () => {
    dispatch({
      type: "NEXT_STEP",
      payload: {
        form,
      },
    });
  };

  const onMediaChanged = (
    media: {
      url: string;
      type: string;
    } | null
  ) => {
    if (media) {
      form.setFieldValue("metadata", {
        ...formValues.metadata,
        mediaUrl: media.url,
        mediaType: typeFromMime[media.type],
      });
    } else {
      form.setFieldValue("metadata", {
        ...formValues.metadata,
        mediaUrl: "",
        mediaType: SupportedMediaType.IMAGE,
        coverMediaUrl: null,
      });
    }
  };

  const onCoverMediaChanged = (
    media: {
      url: string;
      type: string;
    } | null
  ) => {
    if (media) {
      form.setFieldValue("metadata", {
        ...formValues.metadata,
        coverMediaUrl: media.url,
      });
    } else {
      form.setFieldValue("metadata", {
        ...formValues.metadata,
        coverMediaUrl: null,
      });
    }
  };

  const needCoverMedia =
    formValues.metadata.mediaType !== SupportedMediaType.IMAGE;

  const traitsComponent = (
    <>
      {!isEmpty(form.values.metadata.traits) && (
        <CustomPaginationWrapper
          activePage={activePage}
          setPage={setPage}
          setLowerBound={setLowerBound}
          setUpperBound={setUpperBound}
          maxLength={Object.keys(formValues.metadata.traits).length}
        >
          {Object.keys(formValues.metadata.traits)
            .slice(lowerBound - 1, upperBound)
            .map((key, index) => {
              const isLast =
                index === Object.keys(formValues.metadata.traits).length - 1;
              return (
                <Group
                  key={key}
                  position="apart"
                  sx={{
                    borderBottom: isLast
                      ? undefined
                      : `1px solid ${theme.colors.divider[0]}`,
                    height: 48,
                  }}
                >
                  <Text size="sm">{key}</Text>
                  <Group spacing={6}>
                    <Text color={TEXT_COLOR.SECONDARY} size="sm">
                      {formValues.metadata.traits[key]}
                    </Text>
                    <XCircleFill
                      cursor="pointer"
                      onClick={() => {
                        const clearedTraits = formValues.metadata.traits;
                        delete clearedTraits[key];
                        form.setFieldValue("metadata", {
                          ...formValues.metadata,
                          traits: clearedTraits,
                        });
                      }}
                    />
                  </Group>
                </Group>
              );
            })}
        </CustomPaginationWrapper>
      )}
      <Button
        fullWidth
        color={OUTLINE_COLOR}
        size="xl"
        onClick={() => setAddMetadataTraitsModalOpen(true)}
      >
        <Group noWrap spacing="xs">
          <PlusCircle />
          Add a trait
        </Group>
      </Button>
      <Space h="xl" />
      <CheckboxConfirm
        label="Automatically add a metadata trait for the name of this collection. This is helpful for differentiating different collections on one contract."
        mb={10}
        {...form.getInputProps("metadata.generateNameTrait", {
          type: "checkbox",
        })}
      />
    </>
  );

  if (asQuickDropFlow) {
    return (
      <Box>
        <Stack spacing="xs">
          <Text component="label" size="sm">
            Artwork
          </Text>

          <Stack>
            <Box sx={{ flex: 1 }}>
              <CollectionMediaUploader
                width="100%"
                mediaUrl={formValues.metadata.mediaUrl}
                mediaType={
                  formValues.metadata.mediaType === "zip"
                    ? SupportedMediaType.IMAGE
                    : formValues.metadata.mediaType
                }
                onMediaChanged={onMediaChanged}
                mediaError={(form.errors["metadata.mediaUrl"] as string) ?? ""}
              />
            </Box>
            {needCoverMedia && (
              <Box sx={{ flex: 1 }}>
                <CollectionMediaUploader
                  width="100%"
                  mediaUrl={formValues.metadata.coverMediaUrl || undefined}
                  mediaType={SupportedMediaType.IMAGE}
                  onMediaChanged={onCoverMediaChanged}
                  mediaError={
                    (form.errors["metadata.coverMediaUrl"] as string) ?? ""
                  }
                  isCoverImage={true}
                />
              </Box>
            )}
          </Stack>
        </Stack>

        <Portal
          target={"#advanced-options-portal-target"}
          className="optional-portal"
        >
          <Box>
            <Text fw={WEIGHT_BOLD} size="sm" mb={8}>
              Traits
            </Text>
            <Stack spacing={0}>{traitsComponent}</Stack>
          </Box>
        </Portal>

        <AddMetadataTraitsModal
          isOpen={addMetadataTraitsModalOpen}
          onClose={() => setAddMetadataTraitsModalOpen(false)}
          handleSubmit={(newAttr) => {
            const currTraits = formValues.metadata.traits;
            currTraits[newAttr.name] = newAttr.value;
            form.setFieldValue("metadata.traits", currTraits);
            // FIXME: We may need to use the "form list" feature of Mantine form
            // instead of empty object ({}) initial value to correctly set traits dirtyness.
            form.setDirty({
              "metadata.traits": true,
            });
          }}
        />
      </Box>
    );
  }

  return (
    <Box>
      <FormTitle
        title={`Upload ${
          formValues.collectionType === _CollectionType.OneOfOne
            ? "artwork"
            : "collection"
        }`}
        buttons={[
          <SaveDraftButton
            key="draft"
            loading={loading}
            onClick={() => saveDraft()}
          />,
          <NextStepButton key="next" loading={loading} onClick={onNextStep} />,
        ]}
      />

      <Grid>
        <Grid.Col span={12}>
          {formValues.collectionType !== _CollectionType.OneOfOne
            ? "Upload the artwork that will be used for all tokens in the collection. You can optionally add metadata attributes that will also apply to all tokens in the collection."
            : "Upload the artwork that will be used for the token in this one-of-one collection. You can optionally add metadata attributes that will also apply to this token."}
          <Space h="xl" />
        </Grid.Col>

        <Grid.Col span={7}>
          <Text fw={WEIGHT_BOLD} size="sm" mb={8}>
            Traits
          </Text>
          <Stack spacing={0}>
            {traitsComponent}

            <Space h="xl" />
            <Space h="xl" />

            <NextStepButton
              loading={loading}
              onClick={onNextStep}
              disabled={!formValues.metadata.mediaUrl}
            />
          </Stack>
        </Grid.Col>

        <Grid.Col span={1} />
        <Grid.Col span={4}>
          <Stack spacing="xs" maw={256} ml="auto">
            <Text component="label" size="sm">
              Artwork
            </Text>

            <CollectionMediaUploader
              mediaUrl={formValues.metadata.mediaUrl}
              mediaType={
                formValues.metadata.mediaType === "zip"
                  ? SupportedMediaType.IMAGE
                  : formValues.metadata.mediaType
              }
              onMediaChanged={onMediaChanged}
              mediaError={(form.errors["metadata.mediaUrl"] as string) ?? ""}
            />
            {needCoverMedia && (
              <CollectionMediaUploader
                mediaUrl={formValues.metadata.coverMediaUrl || undefined}
                mediaType={SupportedMediaType.IMAGE}
                onMediaChanged={onCoverMediaChanged}
                mediaError={
                  (form.errors["metadata.coverMediaUrl"] as string) ?? ""
                }
                isCoverImage={true}
              />
            )}
          </Stack>
        </Grid.Col>
      </Grid>
      <AddMetadataTraitsModal
        isOpen={addMetadataTraitsModalOpen}
        onClose={() => setAddMetadataTraitsModalOpen(false)}
        handleSubmit={(newAttr) => {
          const currTraits = formValues.metadata.traits;
          currTraits[newAttr.name] = newAttr.value;
          form.setFieldValue("metadata.traits", currTraits);
          // FIXME: We may need to use the "form list" feature of Mantine form
          // instead of empty object ({}) initial value to correctly set traits dirtyness.
          form.setDirty({
            "metadata.traits": true,
          });
        }}
      />
    </Box>
  );
};

export default SelectSingleImageForm;
